import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import EmailPrompt from './EmailPrompt';
import { useMyHomeTiles } from '@sentara-health/sentara-api-hooks-core';
import { GlobalContext, isAvMed } from 'context';
import { useNavigate } from 'react-router-dom';
import {
  ProfileUpdate,
  SHOW_PREFERENCE_POPUP,
  setLocalStorageBoolean,
  getLocalStorageBoolean,
  setSessionStorageBoolean,
  getSessionStorageBoolean,
  GenerateTiles,
  isGuest,
  NavigateConst,
  DeviceTypes,
  useDeviceType,
  WELLNESS_PLAN_CONST,
  ANALYTICS_MESSAGES,
  domainForSegment,
} from 'common';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { DASHBOARD_TILES } from 'common/utils/constants/Constants';
import ApiWrapper from '../../common/ApiWrapper';
import HomeTiles from './HomeTiles';
import {
  ModalOverlay,
  Button,
  Headings,
  Loader,
  Paragraph,
} from '@sentara-health/optimahealth_web';
import { CareTile, PaperlessPreferencePopup } from 'templates';
import { usePreferenceSearchAPI } from 'common/utils/PreferenceSearchAPIUtil';

interface Tile {
  name: string;
  icon: React.JSX.Element;
  id: string;
}

const Dashboard = () => {
  const { getFeature, loginMemberId, planWellness, profileState } =
    useContext(GlobalContext);
  const { MyHomeTilesType } = useMyHomeTiles();
  const navigate = useNavigate();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === DeviceTypes.DESKTOP;
  const [allTiles, setAllTiles] = useState<Tile[]>([]);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const profileUpdate = getLocalStorageBoolean(ProfileUpdate);
  const { data: dashboardData, callApi: callUpdateDashboard }: any =
    ApiWrapper(MyHomeTilesType);
  const effectRan = useRef(false);
  const dashboardEffectRan = useRef(false);
  const { contentListingItemClicked } = useAdobeAnalytics();

  const trackContentListing = useCallback(
    (contentId: string, contentTitle: string, itemPosition: number) => {
      contentListingItemClicked({
        segment: `${domainForSegment}${window.location.pathname}`,
        contentListing: {
          eventType: 'Content Listing Item Clicked',
          listing: {
            content: [
              {
                contentId,
                contentTitle,
                itemPosition,
              },
            ],
          },
        },
      });
    },
    [contentListingItemClicked]
  );

  useEffect(() => {
    (async () => {
      const guestValue: boolean = await isGuest();
      if (dashboardEffectRan.current) return;
      if (getFeature && !guestValue) {
        dashboardEffectRan.current = true;
        callUpdateDashboard();
      }
    })();
  }, [getFeature]);

  /***Generate Tiles and display tiles in dashboard based on launchDarkly*/
  useEffect(() => {
    let tilesToRender = dashboardData;
    if (isAvMed()) {
      tilesToRender = DASHBOARD_TILES;
    }
    const getItems = GenerateTiles(tilesToRender, getFeature, t);
    setAllTiles(getItems);
  }, [dashboardData, getFeature, t]);

  function popupClose() {
    localStorage.setItem(ProfileUpdate, 'false');
    setIsOpen(false);
  }
  useEffect(() => {
    if (profileUpdate) {
      setIsOpen(false);
    }
  }, [profileUpdate]);

  const clickCare = () => {
    trackContentListing('902', ANALYTICS_MESSAGES.FIND_DOCTOR_BANNER_CLICK, 1);
    navigate(NavigateConst.findADoctor);
  };

  const handleBannerLoadError = useCallback(() => {
    trackContentListing(
      '903',
      ANALYTICS_MESSAGES.DASHBOARD_BANNER_LOAD_ERROR,
      2
    );
  }, [trackContentListing]);

  const [smsConsent, setSmsConsent] = useState<boolean>(false);
  const [emailConsent, setEmailConsent] = useState<boolean>(false);

  const { fetchProfile, fetchMemberDetailsAPI } = usePreferenceSearchAPI();

  useEffect(() => {
    (async () => {
      if (effectRan.current) return;
      effectRan.current = true; // Set immediately

      const guestValue: boolean = await isGuest();

      if (!guestValue && loginMemberId && loginMemberId.trim() !== '') {
        fetchMemberDetailsAPI();
        fetchProfile();
      } else {
        effectRan.current = false; // Reset if not calling fetchProfile
      }
    })();
  }, [loginMemberId]);

  useEffect(() => {
    if (
      profileState &&
      (profileState?.smsConsent || profileState?.paperlessConsent)
    ) {
      setSmsConsent(!profileState?.smsConsent?.optInConsent);
      setEmailConsent(!profileState?.paperlessConsent?.optInConsent);
    }
  }, [profileState]);

  function preferencesPopupClose() {
    setSmsConsent(false);
    setEmailConsent(false);
    setSessionStorageBoolean(SHOW_PREFERENCE_POPUP, true);
  }

  const textNotificationsFeature = getFeature?.TextNotifications ?? false;
  const emailConsentFeature = getFeature?.EmailConsent ?? false;
  const communicationPreferencesPopup =
    getFeature?.TextNotificationPopup || getFeature?.EmailConsentPopUp;
  const showBannerImg = isAvMed()
    ? 'hero-img-avmed'
    : getFeature?.WellnessPrograms && planWellness === WELLNESS_PLAN_CONST
    ? 'hero-img-guest-shp'
    : 'hero-img-shp';
  const currentLanguage = i18n.language;
  const languageClass = currentLanguage === 'es' ? 'lang-es' : '';

  return (
    <div>
      {loading && <Loader />}
      <div className={`dashboard mb-4 ${isAvMed() ? 'dashboard-bg-img' : ''}`}>
        <div className="hero-section d-flex">
          {loginMemberId && loginMemberId.trim() !== '' ? (
            <div
              className="profile-id-text pt-2 col-12 visually-hidden"
              data-testid="profileIdBenefits"
            >
              {loginMemberId}
            </div>
          ) : null}

          <div
            data-testid={'heroImgId'}
            className={`hero-img ${showBannerImg} ${languageClass}`}
            onError={handleBannerLoadError}
          >
            <div className="hero-container d-flex flex-column p-sm-0 p-4">
              {getFeature?.FindADoctor && (
                <div className="inner-cont d-flex flex-column">
                  <Headings
                    level={1}
                    text={t('dashboard.careProviderTitle')}
                    variant="semiBold"
                    className="mt-n3"
                  />
                  <Paragraph
                    text={t('dashboard.shortBlurb')}
                    variant="regular"
                    className="mt-n3 mb-1 w-75"
                  />
                  <Button
                    dataTestId={'findCareButton'}
                    variant="primary_btn"
                    onClick={() => clickCare()}
                  >
                    {t('dashboard.findCare')}
                  </Button>
                </div>
              )}

              {getFeature?.WellnessPrograms &&
                planWellness === WELLNESS_PLAN_CONST && (
                  <div className="inner-cont d-flex flex-column w-75">
                    <Headings
                      level={1}
                      text={t('dashboard.welcomeHeading')}
                      data-testid="guestWelcomeText"
                      variant="semiBold"
                    />
                    <Paragraph
                      text={t('dashboard.welcomeInfo')}
                      variant="regular"
                      className="mt-n3 mb-1 w-75"
                    />
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
      {getFeature?.HomeTiles && (
        <div className="grid-cont d-flex">
          <div
            className="left-border-white"
            aria-hidden="true"
            data-focusable="false"
          ></div>
          <HomeTiles allTiles={allTiles} />
          <div
            className="bottom-border-white"
            aria-hidden="true"
            data-focusable="false"
          ></div>
        </div>
      )}
      {getFeature?.WellnessPrograms && planWellness === WELLNESS_PLAN_CONST && (
        <div className="plan-grid-cont d-flex">
            <div
              className="left-border-white"
              aria-hidden="true"
              data-focusable="false"
            ></div>
            <CareTile
              dataTestId="guestWebMD"
              className="align-self-start caretile-border-bottom"
              fontAwesomeIconKey="clipboard-list-check"
              heading={t('dashboard.benefitsTitle')}
              subHeading={t('dashboard.benefitsContent') ?? ''}
              onClick={() => navigate(NavigateConst.enrollBenefits)}
            />
            <CareTile
              dataTestId="guestWebMD"
              className="align-self-start caretile-border-bottom"
              fontAwesomeIconKey="hand-holding-heart"
              heading={t('dashboard.wellnessTitle')}
              subHeading={t('dashboard.wellnessContent') ?? ''}
              onClick={() => navigate(NavigateConst.wellnessProgram)}
            />
            <div
            className="bottom-border-white"
            aria-hidden="true"
            data-focusable="false"
          ></div>
        </div>
      )}

      {getFeature?.UpdateEmailPrompt && isOpen && (
        <ModalOverlay
          isOpen
          onClose={popupClose}
          overlayInfo="Modal information"
        >
          <EmailPrompt setIsOpen={setIsOpen} />
        </ModalOverlay>
      )}
      {!getSessionStorageBoolean(SHOW_PREFERENCE_POPUP) &&
        profileState &&
        (profileState as { isMemberEnrolled: boolean })?.isMemberEnrolled &&
        ((smsConsent &&
          textNotificationsFeature &&
          communicationPreferencesPopup) ||
          (emailConsent &&
            emailConsentFeature &&
            communicationPreferencesPopup)) && (
          <PaperlessPreferencePopup
            smsConsent={smsConsent}
            emailConsent={emailConsent}
            closeModal={preferencesPopupClose}
          />
        )}
    </div>
  );
};
Dashboard.propTypes = {};

export default Dashboard;
